import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button, IconButton, Typography } from '@weave-mui/material';
import { getErrorDetail } from '../../utils/errorDetail';
export const FIELD_LABEL_SX = {
    fontFamily: '"DM Sans", sans-serif',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: 'text.secondary',
};
const NON_TEXT_INPUT_TYPES = new Set(['button', 'checkbox', 'radio', 'submit', 'reset']);
function isTextEntry(target) {
    return target instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.has(target.type);
}
export default function EditableField({ label, hideLabel, view, children, saveDisabled, onEdit, onSave }) {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    function startEditing() {
        onEdit?.();
        setError(null);
        setEditing(true);
    }
    function cancel() {
        setError(null);
        setEditing(false);
    }
    async function save() {
        if (saveDisabled || saving)
            return;
        setError(null);
        setSaving(true);
        try {
            await onSave();
            setEditing(false);
        }
        catch (err) {
            setError(getErrorDetail(err));
        }
        finally {
            setSaving(false);
        }
    }
    function handleKeyDown(e) {
        if (e.key === 'Escape') {
            // Stops the sidebar's window-level Escape-to-close from also firing.
            e.stopPropagation();
            cancel();
            return;
        }
        // Text inputs only: buttons and toggles need Enter to activate themselves, textareas need it
        // for newlines, and Autocomplete marks it defaultPrevented when Enter commits a chip.
        if (e.key === 'Enter' && !e.defaultPrevented && isTextEntry(e.target)) {
            e.preventDefault();
            save();
        }
    }
    if (!editing) {
        return (_jsxs(Box, { sx: { '&:hover .editable-field-pencil': { opacity: 1 } }, children: [!hideLabel && _jsx(Typography, { sx: { ...FIELD_LABEL_SX, mb: 0.25 }, children: label }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 0.5 }, children: [_jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: view }), _jsx(IconButton, { className: "editable-field-pencil", size: "small", "aria-label": `Edit ${label}`, onClick: startEditing, sx: { mt: -0.5, color: 'text.secondary', opacity: 0.55, '&:focus-visible': { opacity: 1 } }, children: _jsx(EditOutlinedIcon, { sx: { fontSize: '0.95rem' } }) })] })] }));
    }
    return (_jsxs(Box, { role: "group", "aria-label": `Editing ${label}`, onKeyDown: handleKeyDown, sx: { p: 1, mx: -1, borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.025)', border: '1px solid rgba(0,0,0,0.06)' }, children: [!hideLabel && _jsx(Typography, { sx: { ...FIELD_LABEL_SX, mb: 0.75 }, children: label }), children, error && (_jsxs(Typography, { role: "alert", sx: { mt: 0.75, fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: 'error.main' }, children: ["Couldn't save: ", error] })), _jsxs(Box, { sx: { display: 'flex', justifyContent: 'flex-end', gap: 0.75, mt: 1 }, children: [_jsx(Button, { size: "small", "aria-label": `Cancel editing ${label}`, onClick: cancel, sx: { textTransform: 'none', fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem' }, children: "Cancel" }), _jsx(Button, { size: "small", variant: "contained", "aria-label": `Save ${label}`, disabled: saveDisabled || saving, onClick: save, sx: { textTransform: 'none', fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem' }, children: saving ? 'Saving…' : 'Save' })] })] }));
}
